import { inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { NodeAssociationEntry } from '@alfresco/js-api';

import { SnackbarErrorAction } from '../store/actions/app.custom-actions';
import { AppStore } from '../store/states/app.state';
import { WkErrorResponse } from '../model/wk-error-response';
import { AppConstants } from '../components/shared/constants/app-constants';
import { WkAlfrescoApiService } from '../services/wk-alfresco-api.service';

export const wkPreviewRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const previewRegExp = /(?<=preview\/)(?!s\/)[^\/?#]+(?=.*)/;
  const wkAlfrescoApi = inject(WkAlfrescoApiService);
  const router = inject(Router);
  const store = inject(Store<AppStore>);

  function redirectWorkingCopy(
    previewUrl: string,
    workingCopyId: string
  ): void {
    const newUrl = replaceNodeId(previewUrl, workingCopyId);
    router.navigateByUrl(newUrl, {
      state: { afterPreviewRedirect: true },
    });
  }

  function findWorkingCopy(
    entries: NodeAssociationEntry[]
  ): NodeAssociationEntry | undefined {
    return entries.find(
      ({ entry }) =>
        entry.association.assocType === AppConstants.WORKING_COPY_LINK
    );
  }

  function replaceNodeId(url: string, id: string): string {
    return url.replace(previewRegExp, id);
  }

  function isAfterPreviewRedirect(): boolean {
    return Boolean(
      router.getCurrentNavigation()?.extras.state?.afterPreviewRedirect
    );
  }

  function handleError(error: HttpErrorResponse): Observable<boolean> {
    let errorResponse: WkErrorResponse;
    try {
      errorResponse = JSON.parse(error.message);
    } catch {
      return of(true);
    }
    if (errorResponse?.error?.statusCode === 404) {
      store.dispatch(
        new SnackbarErrorAction('APP.MESSAGES.ERRORS.MISSING_CONTENT')
      );
      return of(false);
    }
    return of(true);
  }

  const { nodeId } = route.params;
  const afterPreviewRedirect = isAfterPreviewRedirect();

  return (
    afterPreviewRedirect ||
    from(
      wkAlfrescoApi.nodesApi.listTargetAssociations(nodeId, {
        where: `(assocType=${AppConstants.WORKING_COPY_LINK})`,
      })
    ).pipe(
      map(({ list: { entries } }) => {
        const workingCopy = findWorkingCopy(entries);
        if (workingCopy) {
          redirectWorkingCopy(state.url, workingCopy.entry.id);
          return false;
        }
        return true;
      }),
      catchError((error) => handleError(error))
    )
  );
};
